import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Trash2, Eye, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, SectionCard } from "@/components/app/PageKit";

export default function Settings() {
  const { user, settings, refreshSettings } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef();
  const [previewOpen, setPreviewOpen] = useState(false);
  const bgRef = useRef();
  const [form, setForm] = useState({
    software_name: settings.software_name || "QrewHR",
    tagline: settings.tagline || "",
    logo_initial: settings.logo_initial || "HE",
    logo_url: settings.logo_url || "",
    primary_color: settings.primary_color || "#0F172A",
    accent_color: settings.accent_color || "#06B6D4",
    login_hero_title: settings.login_hero_title || "One platform to see, manage, and grow your people.",
    login_hero_subtitle: settings.login_hero_subtitle || "QrewHR unifies HRMS, attendance, approvals, tasks, projects, assets, and AI insights into a single premium experience.",
    login_welcome_title: settings.login_welcome_title || "Welcome back",
    login_welcome_subtitle: settings.login_welcome_subtitle || "Sign in to your Qrew HR workspace",
    login_background_url: settings.login_background_url || "",
    login_badges: (settings.login_badges || ["15+ modules", "AI-first", "API-ready"]).join(", "),
    login_show_quick_login: settings.login_show_quick_login !== false,
    // Login gradient/overlay (Phase 9c)
    login_gradient_preset: settings.login_gradient_preset || "midnight",
    login_gradient_from: settings.login_gradient_from || "#0f172a",
    login_gradient_via: settings.login_gradient_via || "#1e3a8a",
    login_gradient_to: settings.login_gradient_to || "#4c1d95",
    login_gradient_angle: settings.login_gradient_angle ?? 135,
    login_overlay_opacity: settings.login_overlay_opacity ?? 0.55,
    // Theme & branding (new in Phase 9)
    theme_palette: settings.theme_palette || "cyan",
    font_family: settings.font_family || "Inter",
    base_font_size: settings.base_font_size || "14px",
    // Org locale (for masters)
    country: settings.country || "IN",
    currency: settings.currency || "INR",
    default_work_mode: settings.default_work_mode || "hybrid",
    // Work Schedule (Round 11)
    working_days_pattern: settings.working_days_pattern || "5",
    working_days: settings.working_days || ["mon","tue","wed","thu","fri"],
    alternate_saturday_policy: settings.alternate_saturday_policy || "none",
    shift_start_time: settings.shift_start_time || "09:00",
    shift_end_time: settings.shift_end_time || "18:00",
    timezone: settings.timezone || "Asia/Kolkata",
    lunch_break_min: settings.lunch_break_min ?? 60,
    // ============== Sprint 1 — Design System ==============
    btn_gradient_from: settings.btn_gradient_from || "#2563EB",
    btn_gradient_via:  settings.btn_gradient_via  || "#6D28D9",
    btn_gradient_to:   settings.btn_gradient_to   || "#D946EF",
    btn_text_color:    settings.btn_text_color    || "#FFFFFF",
    btn_radius:        settings.btn_radius        || "10px",
    btn_shadow:        settings.btn_shadow !== false,
    color_success:     settings.color_success || "#10B981",
    color_warning:     settings.color_warning || "#F59E0B",
    color_danger:      settings.color_danger  || "#EF4444",
    color_info:        settings.color_info    || "#3B82F6",
    card_radius:       settings.card_radius || "12px",
    sidebar_mode:      settings.sidebar_mode || "light",
    dashboard_density: settings.dashboard_density || "comfortable",
    table_density:     settings.table_density || "comfortable",
    show_module_badges: settings.show_module_badges !== false,
    heading_style:     settings.heading_style || "modern",
    // Payslip
    payslip_logo_url:    settings.payslip_logo_url || "",
    payslip_header_text: settings.payslip_header_text || "Payslip",
    payslip_footer_text: settings.payslip_footer_text || "This is a system-generated payslip.",
    payslip_primary:     settings.payslip_primary || "#0F172A",
    payslip_accent:      settings.payslip_accent  || "#6D28D9",
    payslip_layout:      settings.payslip_layout  || "modern",
    payslip_show_address:   settings.payslip_show_address !== false,
    payslip_show_signature: settings.payslip_show_signature !== false,
    payslip_show_qr:        settings.payslip_show_qr !== false,
    // Login extras
    login_show_google:    settings.login_show_google !== false,
    login_show_microsoft: settings.login_show_microsoft === true,
    login_show_signup:    settings.login_show_signup !== false,
    login_show_workspace: settings.login_show_workspace !== false,
    login_trust_badges:   (settings.login_trust_badges || ["SOC 2 Type II", "GDPR Ready", "ISO 27001"]).join(", "),
    // Attendance providers
    geo_radius_m:         settings.geo_radius_m ?? 200,
    selfie_required:      settings.selfie_required !== false,
    device_verification:  settings.device_verification || "trusted",
  });

  const canEdit = user?.role === "super_admin";
  if (!canEdit) {
    // Non-super admins redirected away
    setTimeout(() => nav("/"), 0);
    return <div className="p-8 text-slate-500">Redirecting…</div>;
  }

  const save = async () => {
    try {
      const payload = {
        ...form,
        login_badges: form.login_badges.split(",").map(s => s.trim()).filter(Boolean),
        login_trust_badges: (form.login_trust_badges || "").split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.patch("/settings", payload);
      await refreshSettings();
      toast.success("Settings updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onBgPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 900_000) { toast.error("Background too large (max 900KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, login_background_url: reader.result });
    reader.readAsDataURL(f);
  };

  const onLogoPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 300_000) { toast.error("Logo too large (max 300KB)"); return; }
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo_url: reader.result });
    reader.readAsDataURL(f);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-4xl overflow-x-hidden" data-testid="settings-page">
      <PageHeader
        category="Administration"
        title="Settings"
        description="Branding, login experience, theme, locale and work schedule — Super Admin only"
        primaryAction={{
          label: "Save All",
          icon: Save,
          onClick: save,
          testid: "settings-save-all",
        }}
      />

      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Logo</div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-20 h-20 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
            {form.logo_url
              ? <img src={form.logo_url} alt="logo" className="w-full h-full object-contain"/>
              : <span className="text-2xl font-[Outfit] font-bold text-slate-400">{form.logo_initial}</span>}
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onLogoPick} data-testid="logo-input"/>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="logo-upload-btn">
                <Upload className="w-3.5 h-3.5 mr-1"/>Upload logo
              </Button>
              {form.logo_url && (
                <Button variant="outline" size="sm" onClick={() => setForm({ ...form, logo_url: "" })} className="text-rose-600" data-testid="logo-remove-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1"/>Remove
                </Button>
              )}
              {form.logo_url && (
                <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(true)} data-testid="logo-preview-btn"><Eye className="w-3.5 h-3.5 mr-1"/>Preview</Button>
              )}
            </div>
            <p className="text-[11px] text-slate-500">PNG, JPG or SVG, up to 300KB. Recommended 256×256.</p>
          </div>
        </div>
      </Card>

      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Branding</div>
        <div><Label>Software Name</Label><Input value={form.software_name} onChange={(e) => setForm({...form, software_name: e.target.value})} className="mt-1" data-testid="settings-name"/></div>
        <div><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({...form, tagline: e.target.value})} className="mt-1" data-testid="settings-tagline"/></div>
        <div><Label>Logo Initial fallback (2 chars)</Label><Input maxLength={2} value={form.logo_initial} onChange={(e) => setForm({...form, logo_initial: e.target.value.toUpperCase()})} className="mt-1 w-24"/></div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Primary Color</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input type="color" value={form.primary_color || "#0F172A"} onChange={(e) => setForm({...form, primary_color: e.target.value})} className="h-11 w-16 rounded-md border border-slate-200 cursor-pointer p-0.5 bg-white shrink-0" data-testid="primary-color" />
              <Input value={form.primary_color} onChange={(e) => setForm({...form, primary_color: e.target.value})} placeholder="#0F172A" className="flex-1 h-11 font-mono text-xs uppercase min-w-0" />
            </div>
          </div>
          <div>
            <Label>Accent Color</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input type="color" value={form.accent_color || "#06B6D4"} onChange={(e) => setForm({...form, accent_color: e.target.value})} className="h-11 w-16 rounded-md border border-slate-200 cursor-pointer p-0.5 bg-white shrink-0" data-testid="accent-color" />
              <Input value={form.accent_color} onChange={(e) => setForm({...form, accent_color: e.target.value})} placeholder="#06B6D4" className="flex-1 h-11 font-mono text-xs uppercase min-w-0" />
            </div>
          </div>
        </div>

        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="settings-save">Save Branding</Button>
      </Card>

      {/* ========== BUTTONS & GRADIENT ========== */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="buttons-panel">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Buttons &amp; Gradient</div>
        <p className="text-xs text-slate-500 -mt-2">Controls the primary call-to-action style across login, dashboard, modals, mobile bottom nav and platform console.</p>

        {/* Live preview */}
        <div
          className="rounded-xl p-4 bg-slate-50 border border-slate-200 flex items-center gap-3 flex-wrap"
          data-testid="btn-preview"
        >
          <button
            className="qh-btn"
            style={{
              backgroundImage: `linear-gradient(90deg, ${form.btn_gradient_from}, ${form.btn_gradient_via}, ${form.btn_gradient_to})`,
              color: form.btn_text_color,
              borderRadius: form.btn_radius,
              boxShadow: form.btn_shadow ? "0 6px 14px -4px rgba(99,67,217,.35), 0 2px 4px rgba(15,23,42,.06)" : "none",
              padding: "0 18px", height: 40, fontWeight: 600, fontSize: 13.5,
            }}
          >Primary CTA</button>
          <span className="qh-btn btn-secondary" style={{ borderRadius: form.btn_radius }}>Secondary</span>
          <span className="qh-btn btn-soft" style={{ borderRadius: form.btn_radius }}>Soft</span>
          <span className="qh-btn btn-success" style={{ borderRadius: form.btn_radius, color: "#fff" }}>Success</span>
          <span className="qh-btn btn-danger" style={{ borderRadius: form.btn_radius, color: "#fff" }}>Danger</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ColorPick label="Gradient · Start" value={form.btn_gradient_from} onChange={(v) => setForm({ ...form, btn_gradient_from: v })} testId="btn-grad-from" />
          <ColorPick label="Gradient · Middle" value={form.btn_gradient_via} onChange={(v) => setForm({ ...form, btn_gradient_via: v })} testId="btn-grad-via" />
          <ColorPick label="Gradient · End" value={form.btn_gradient_to} onChange={(v) => setForm({ ...form, btn_gradient_to: v })} testId="btn-grad-to" />
        </div>

        {/* Gradient presets */}
        <div>
          <Label className="text-xs">Quick presets</Label>
          <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { k: "qrew",     l: "Qrew",      from: "#2563EB", via: "#6D28D9", to: "#D946EF" },
              { k: "midnight", l: "Midnight",  from: "#0F172A", via: "#1E3A8A", to: "#4C1D95" },
              { k: "ocean",    l: "Ocean",     from: "#0EA5E9", via: "#06B6D4", to: "#14B8A6" },
              { k: "sunset",   l: "Sunset",    from: "#F97316", via: "#EC4899", to: "#A855F7" },
              { k: "emerald",  l: "Emerald",   from: "#10B981", via: "#0EA5E9", to: "#6366F1" },
              { k: "fire",     l: "Fire",      from: "#EF4444", via: "#F59E0B", to: "#FBBF24" },
              { k: "mono",     l: "Monochrome",from: "#0F172A", via: "#334155", to: "#64748B" },
              { k: "rose",     l: "Rose",      from: "#E11D48", via: "#9F1239", to: "#7F1D1D" },
            ].map((p) => (
              <button
                key={p.k}
                onClick={() => setForm({ ...form, btn_gradient_from: p.from, btn_gradient_via: p.via, btn_gradient_to: p.to })}
                className="rounded-lg border border-slate-200 p-2 flex items-center gap-2 hover:border-slate-300"
                data-testid={`btn-preset-${p.k}`}
              >
                <span className="w-7 h-7 rounded-md shrink-0" style={{ backgroundImage: `linear-gradient(90deg, ${p.from}, ${p.via}, ${p.to})` }} />
                <span className="text-[12px] font-semibold text-slate-700 truncate">{p.l}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Text color</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input type="color" value={form.btn_text_color} onChange={(e) => setForm({ ...form, btn_text_color: e.target.value })} className="h-10 w-14 rounded border border-slate-200 cursor-pointer p-0.5 bg-white shrink-0" data-testid="btn-text-color" />
              <Input value={form.btn_text_color} onChange={(e) => setForm({ ...form, btn_text_color: e.target.value })} className="flex-1 h-10 font-mono text-xs uppercase min-w-0" />
            </div>
          </div>
          <div>
            <Label>Button radius</Label>
            <select value={form.btn_radius} onChange={(e) => setForm({ ...form, btn_radius: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="btn-radius">
              <option value="6px">Sharp (6px)</option>
              <option value="8px">Subtle (8px)</option>
              <option value="10px">Default (10px)</option>
              <option value="14px">Soft (14px)</option>
              <option value="9999px">Pill (rounded)</option>
            </select>
          </div>
          <div>
            <Label>Drop shadow</Label>
            <div className="mt-1.5 flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-white">
              <input type="checkbox" checked={form.btn_shadow} onChange={(e) => setForm({ ...form, btn_shadow: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="btn-shadow" />
              <span className="text-sm text-slate-700">Show subtle drop-shadow on primary buttons</span>
            </div>
          </div>
        </div>

        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-buttons">Save Buttons</Button>
      </Card>

      {/* ========== STATUS COLOR PALETTE ========== */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="status-palette-panel">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Status Palette</div>
        <p className="text-xs text-slate-500 -mt-2">Used by badges, alerts, KPIs and form-state indicators across every module.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ColorPick label="Success" value={form.color_success} onChange={(v) => setForm({ ...form, color_success: v })} testId="color-success" />
          <ColorPick label="Warning" value={form.color_warning} onChange={(v) => setForm({ ...form, color_warning: v })} testId="color-warning" />
          <ColorPick label="Danger"  value={form.color_danger}  onChange={(v) => setForm({ ...form, color_danger: v })}  testId="color-danger" />
          <ColorPick label="Info"    value={form.color_info}    onChange={(v) => setForm({ ...form, color_info: v })}    testId="color-info" />
        </div>
        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-palette">Save Palette</Button>
      </Card>

      {/* ========== LAYOUT & DENSITY ========== */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="layout-panel">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Layout &amp; Density</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label>Card radius</Label>
            <select value={form.card_radius} onChange={(e) => setForm({ ...form, card_radius: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="card-radius">
              <option value="8px">Sharp (8px)</option>
              <option value="12px">Default (12px)</option>
              <option value="16px">Soft (16px)</option>
              <option value="20px">Pillow (20px)</option>
            </select>
          </div>
          <div>
            <Label>Sidebar mode</Label>
            <select value={form.sidebar_mode} onChange={(e) => setForm({ ...form, sidebar_mode: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="sidebar-mode">
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
          <div>
            <Label>Dashboard density</Label>
            <select value={form.dashboard_density} onChange={(e) => setForm({ ...form, dashboard_density: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="dashboard-density">
              <option value="compact">Compact</option>
              <option value="comfortable">Comfortable</option>
              <option value="spacious">Spacious</option>
            </select>
          </div>
          <div>
            <Label>Table density</Label>
            <select value={form.table_density} onChange={(e) => setForm({ ...form, table_density: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="table-density">
              <option value="compact">Compact</option>
              <option value="comfortable">Comfortable</option>
            </select>
          </div>
          <div>
            <Label>Heading style</Label>
            <select value={form.heading_style} onChange={(e) => setForm({ ...form, heading_style: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="heading-style">
              <option value="modern">Modern (Outfit)</option>
              <option value="classic">Classic (Inter)</option>
            </select>
          </div>
          <div>
            <Label>Module badges</Label>
            <div className="mt-1.5 flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-white">
              <input type="checkbox" checked={form.show_module_badges} onChange={(e) => setForm({ ...form, show_module_badges: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="show-module-badges" />
              <span className="text-sm text-slate-700">Show "Coming Soon / Beta / Live" chips</span>
            </div>
          </div>
        </div>
        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-layout">Save Layout</Button>
      </Card>

      {/* ========== PAYSLIP / PDF BRANDING ========== */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="payslip-panel">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Payslip &amp; PDF Branding</div>
        <p className="text-xs text-slate-500 -mt-2">Applied to monthly payslips, expense reports and PDF exports across the workspace.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Payslip header text</Label>
            <Input value={form.payslip_header_text} onChange={(e) => setForm({ ...form, payslip_header_text: e.target.value })} className="mt-1" data-testid="payslip-header" />
          </div>
          <div>
            <Label>Payslip footer text</Label>
            <Input value={form.payslip_footer_text} onChange={(e) => setForm({ ...form, payslip_footer_text: e.target.value })} className="mt-1" data-testid="payslip-footer" />
          </div>
          <ColorPick label="Payslip primary" value={form.payslip_primary} onChange={(v) => setForm({ ...form, payslip_primary: v })} testId="payslip-primary" />
          <ColorPick label="Payslip accent"  value={form.payslip_accent}  onChange={(v) => setForm({ ...form, payslip_accent: v })}  testId="payslip-accent" />
          <div>
            <Label>Layout</Label>
            <select value={form.payslip_layout} onChange={(e) => setForm({ ...form, payslip_layout: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="payslip-layout">
              <option value="classic">Classic</option>
              <option value="modern">Modern</option>
              <option value="compact">Compact</option>
            </select>
          </div>
          <div>
            <Label>Payslip logo URL</Label>
            <Input value={form.payslip_logo_url} onChange={(e) => setForm({ ...form, payslip_logo_url: e.target.value })} placeholder="Defaults to main logo" className="mt-1" data-testid="payslip-logo" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { k: "payslip_show_address",   l: "Show company address" },
            { k: "payslip_show_signature", l: "Show signature block" },
            { k: "payslip_show_qr",        l: "Show QR / verification" },
          ].map(o => (
            <label key={o.k} className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-white cursor-pointer" data-testid={`opt-${o.k}`}>
              <input type="checkbox" checked={form[o.k]} onChange={(e) => setForm({ ...form, [o.k]: e.target.checked })} className="w-4 h-4 accent-violet-600" />
              <span className="text-sm text-slate-700">{o.l}</span>
            </label>
          ))}
        </div>

        {/* Payslip preview card */}
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="payslip-preview">
          <div className="px-5 py-3 flex items-center justify-between" style={{ background: form.payslip_primary, color: "#fff" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/15 rounded-md flex items-center justify-center text-sm font-bold">{form.logo_initial}</div>
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-80">{form.software_name}</div>
                <div className="text-base font-bold">{form.payslip_header_text}</div>
              </div>
            </div>
            <div className="text-[11px] opacity-80">May 2026 · {form.payslip_layout}</div>
          </div>
          <div className="p-4 bg-white grid grid-cols-2 gap-3 text-[12px]">
            <div><span className="text-slate-500">Employee:</span> Tanvi Joshi</div>
            <div><span className="text-slate-500">PF / UAN:</span> 1010 1010 1010</div>
            <div><span className="text-slate-500">Designation:</span> Frontend Engineer</div>
            <div><span className="text-slate-500">Net pay:</span> <span className="font-bold" style={{ color: form.payslip_accent }}>₹84,500</span></div>
          </div>
          {form.payslip_show_signature && (
            <div className="px-5 py-2 text-[11px] text-slate-500 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <span>Authorised signatory</span>
              {form.payslip_show_qr && <span className="px-2 py-1 rounded bg-white border border-slate-200 text-[9px] font-mono">QR-VERIFY</span>}
            </div>
          )}
          <div className="px-5 py-2 text-[10px] text-slate-500 bg-white text-center border-t border-slate-100">{form.payslip_footer_text}</div>
        </div>

        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-payslip">Save Payslip Branding</Button>
      </Card>

      {/* Theme Presets */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="theme-panel">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Theme &amp; Typography</div>

        <div>
          <Label>Color palette</Label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
            {[
              { key: "cyan",    color: "#06b6d4", label: "Cyan" },
              { key: "violet",  color: "#8b5cf6", label: "Violet" },
              { key: "emerald", color: "#10b981", label: "Emerald" },
              { key: "rose",    color: "#f43f5e", label: "Rose" },
              { key: "amber",   color: "#f59e0b", label: "Amber" },
              { key: "indigo",  color: "#6366f1", label: "Indigo" },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => setForm({ ...form, theme_palette: p.key })}
                className={`rounded-xl border p-3 flex flex-col items-center gap-2 transition ${form.theme_palette === p.key ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200 hover:border-slate-300"}`}
                data-testid={`theme-${p.key}`}
              >
                <span className="w-8 h-8 rounded-full shadow-sm" style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}cc)` }} />
                <span className="text-[11px] text-slate-700">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Font family</Label>
            <select value={form.font_family} onChange={(e) => setForm({ ...form, font_family: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="font-family">
              <option value="Inter">Inter (default)</option>
              <option value="Outfit">Outfit (modern)</option>
              <option value="Manrope">Manrope (friendly)</option>
              <option value="Plus Jakarta Sans">Plus Jakarta Sans (premium)</option>
              <option value="DM Sans">DM Sans (clean)</option>
            </select>
          </div>
          <div>
            <Label>Base font size</Label>
            <select value={form.base_font_size} onChange={(e) => setForm({ ...form, base_font_size: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="font-size">
              <option value="13px">Compact (13px)</option>
              <option value="14px">Default (14px)</option>
              <option value="15px">Comfortable (15px)</option>
              <option value="16px">Large (16px)</option>
            </select>
          </div>
        </div>

        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-theme">Save Theme</Button>
      </Card>

      {/* Organization Locale */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="locale-panel">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Organization Locale</div>
        <p className="text-xs text-slate-500 -mt-2">Default country, currency and work-mode applied to new employees, payroll, and reports.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Country</Label>
            <select value={form.country} onChange={(e) => { const c = e.target.value; const ccy = { IN: "INR", US: "USD", GB: "GBP", AE: "AED", SG: "SGD", AU: "AUD", CA: "CAD", DE: "EUR" }[c] || form.currency; setForm({ ...form, country: c, currency: ccy }); }} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="loc-country">
              <option value="IN">India (IN, +91)</option>
              <option value="US">United States (US, +1)</option>
              <option value="GB">United Kingdom (GB, +44)</option>
              <option value="AE">United Arab Emirates (AE, +971)</option>
              <option value="SG">Singapore (SG, +65)</option>
              <option value="AU">Australia (AU, +61)</option>
              <option value="CA">Canada (CA, +1)</option>
              <option value="DE">Germany (DE, +49)</option>
            </select>
          </div>
          <div>
            <Label>Currency</Label>
            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="loc-currency">
              <option value="INR">INR · ₹</option><option value="USD">USD · $</option><option value="EUR">EUR · €</option>
              <option value="GBP">GBP · £</option><option value="AED">AED · د.إ</option><option value="SGD">SGD · S$</option>
              <option value="AUD">AUD · A$</option><option value="CAD">CAD · C$</option>
            </select>
          </div>
          <div>
            <Label>Default work mode</Label>
            <select value={form.default_work_mode} onChange={(e) => setForm({ ...form, default_work_mode: e.target.value })} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="loc-workmode">
              <option value="on_site">On-site</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>
        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-locale">Save Locale</Button>
      </Card>

      {/* ===== Work Schedule ===== */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="work-schedule-card">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Work Schedule</div>
            <div className="text-base font-semibold text-slate-900 mt-1">Office timings, working days &amp; Saturday policy</div>
            <p className="text-xs text-slate-500 mt-0.5">Controls the shift progress ring, lateness calculation, calendar holidays and payroll working-day math.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Working-days pattern</Label>
            <select
              value={form.working_days_pattern}
              onChange={(e) => {
                const p = e.target.value;
                const dayMap = {
                  "5":      ["mon","tue","wed","thu","fri"],
                  "5.5":    ["mon","tue","wed","thu","fri","sat"],
                  "6":      ["mon","tue","wed","thu","fri","sat"],
                };
                setForm({ ...form, working_days_pattern: p, working_days: dayMap[p] || form.working_days });
              }}
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="ws-pattern">
              <option value="5">5 days (Mon–Fri)</option>
              <option value="5.5">5.5 days (Mon–Fri + alt Sat)</option>
              <option value="6">6 days (Mon–Sat)</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <Label>Shift start</Label>
            <Input type="time" value={form.shift_start_time} onChange={(e) => setForm({...form, shift_start_time: e.target.value})} className="mt-1.5" data-testid="ws-start"/>
          </div>
          <div>
            <Label>Shift end</Label>
            <Input type="time" value={form.shift_end_time} onChange={(e) => setForm({...form, shift_end_time: e.target.value})} className="mt-1.5" data-testid="ws-end"/>
          </div>
        </div>

        <div>
          <Label>Working days</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              {k:"mon",l:"Mon"},{k:"tue",l:"Tue"},{k:"wed",l:"Wed"},{k:"thu",l:"Thu"},{k:"fri",l:"Fri"},{k:"sat",l:"Sat"},{k:"sun",l:"Sun"}
            ].map(d => {
              const on = form.working_days.includes(d.k);
              return (
                <button
                  key={d.k} type="button"
                  onClick={() => {
                    const wd = on ? form.working_days.filter(x => x !== d.k) : [...form.working_days, d.k];
                    setForm({ ...form, working_days: wd, working_days_pattern: "custom" });
                  }}
                  data-testid={`ws-day-${d.k}`}
                  className={`h-9 w-14 rounded-md text-sm font-semibold transition border ${on ? "bg-cyan-500 border-cyan-500 text-white shadow-sm" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
                >{d.l}</button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Saturday policy</Label>
            <select
              value={form.alternate_saturday_policy}
              onChange={(e) => setForm({...form, alternate_saturday_policy: e.target.value})}
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" data-testid="ws-sat-policy">
              <option value="none">All Saturdays working (or off, per day toggles)</option>
              <option value="2nd_4th_off">2nd &amp; 4th Saturday OFF</option>
              <option value="1st_3rd_off">1st &amp; 3rd Saturday OFF</option>
              <option value="all_off">All Saturdays OFF</option>
            </select>
            <p className="text-[11px] text-slate-500 mt-1">Applied by calendar / payroll when computing working days.</p>
          </div>
          <div>
            <Label>Lunch break (min)</Label>
            <Input type="number" min="0" max="180" value={form.lunch_break_min} onChange={(e) => setForm({...form, lunch_break_min: Number(e.target.value)})} className="mt-1.5" data-testid="ws-lunch"/>
          </div>
        </div>

        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-schedule">Save Work Schedule</Button>
      </Card>

      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="login-customization">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Login Page</div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">Show demo quick-login</span>
            <input type="checkbox" checked={form.login_show_quick_login} onChange={(e) => setForm({...form, login_show_quick_login: e.target.checked})} data-testid="toggle-quick-login" className="w-4 h-4 accent-cyan-600"/>
          </div>
        </div>

        <div><Label>Welcome title</Label><Input value={form.login_welcome_title} onChange={(e) => setForm({...form, login_welcome_title: e.target.value})} className="mt-1" data-testid="login-welcome-title"/></div>
        <div><Label>Welcome subtitle</Label><Input value={form.login_welcome_subtitle} onChange={(e) => setForm({...form, login_welcome_subtitle: e.target.value})} className="mt-1"/></div>
        <div><Label>Hero title (right panel)</Label><Input value={form.login_hero_title} onChange={(e) => setForm({...form, login_hero_title: e.target.value})} className="mt-1" data-testid="login-hero-title"/></div>
        <div><Label>Hero subtitle</Label><Input value={form.login_hero_subtitle} onChange={(e) => setForm({...form, login_hero_subtitle: e.target.value})} className="mt-1"/></div>
        <div><Label>Badges (comma separated, up to 3)</Label><Input value={form.login_badges} onChange={(e) => setForm({...form, login_badges: e.target.value})} className="mt-1"/></div>

        <div>
          <Label>Background image</Label>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-24 h-16 rounded-md bg-slate-100 border border-slate-200 overflow-hidden">
              {form.login_background_url
                ? <img src={form.login_background_url} alt="bg" className="w-full h-full object-cover"/>
                : <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">default</div>}
            </div>
            <input type="file" accept="image/*" ref={bgRef} className="hidden" onChange={onBgPick} data-testid="bg-input"/>
            <Button type="button" variant="outline" size="sm" onClick={() => bgRef.current?.click()} data-testid="bg-upload-btn"><Upload className="w-3.5 h-3.5 mr-1"/>Upload</Button>
            {form.login_background_url && <Button type="button" variant="ghost" size="sm" onClick={() => setForm({...form, login_background_url: ""})} className="text-red-600">Clear</Button>}
            <div className="flex-1"/>
            <Input value={form.login_background_url} onChange={(e) => setForm({...form, login_background_url: e.target.value})} placeholder="Or paste image URL" className="max-w-xs"/>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Max 900KB. Recommended 1920×1080, subtle office/team imagery.</p>
        </div>

        {/* Gradient editor */}
        <div className="pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <Label>Hero gradient &amp; overlay</Label>
              <p className="text-[11px] text-slate-500 mt-0.5">Pick a preset or build a custom gradient. White hero text stays readable via the dark overlay slider.</p>
            </div>
            <select
              value={form.login_gradient_preset}
              onChange={(e) => {
                const p = e.target.value;
                const presets = {
                  midnight: { from: "#0f172a", via: "#1e3a8a", to: "#4c1d95" },
                  ocean:    { from: "#0c4a6e", via: "#0e7490", to: "#164e63" },
                  aurora:   { from: "#111827", via: "#065f46", to: "#134e4a" },
                  sunset:   { from: "#450a0a", via: "#7c2d12", to: "#881337" },
                  forest:   { from: "#0f172a", via: "#064e3b", to: "#052e16" },
                  royal:    { from: "#1e1b4b", via: "#4c1d95", to: "#581c87" },
                };
                if (p !== "custom" && presets[p]) {
                  setForm({ ...form, login_gradient_preset: p, ...presets[p] });
                } else {
                  setForm({ ...form, login_gradient_preset: p });
                }
              }}
              className="h-9 rounded-md border border-slate-200 bg-white text-sm px-2"
              data-testid="gradient-preset"
            >
              <option value="midnight">Midnight</option>
              <option value="ocean">Ocean</option>
              <option value="aurora">Aurora</option>
              <option value="sunset">Sunset</option>
              <option value="forest">Forest</option>
              <option value="royal">Royal</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Live preview */}
          <div
            className="mt-3 h-24 rounded-xl border border-slate-200 relative overflow-hidden"
            style={{ backgroundImage: `linear-gradient(${form.login_gradient_angle}deg, ${form.login_gradient_from}, ${form.login_gradient_via}, ${form.login_gradient_to})` }}
            data-testid="gradient-preview"
          >
            <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${form.login_overlay_opacity})` }} />
            <div className="relative h-full flex items-center justify-center text-white font-[Outfit] text-lg font-semibold drop-shadow">
              Preview · {form.login_gradient_preset}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ColorPick label="From" value={form.login_gradient_from} onChange={(v) => setForm({ ...form, login_gradient_from: v, login_gradient_preset: "custom" })} testId="gradient-from" />
            <ColorPick label="Via" value={form.login_gradient_via} onChange={(v) => setForm({ ...form, login_gradient_via: v, login_gradient_preset: "custom" })} testId="gradient-via" />
            <ColorPick label="To" value={form.login_gradient_to} onChange={(v) => setForm({ ...form, login_gradient_to: v, login_gradient_preset: "custom" })} testId="gradient-to" />
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Angle · {form.login_gradient_angle}°</Label>
              <input
                type="range" min="0" max="360" step="1"
                value={form.login_gradient_angle}
                onChange={(e) => setForm({ ...form, login_gradient_angle: Number(e.target.value) })}
                className="w-full accent-cyan-600 mt-1"
                data-testid="gradient-angle"
              />
            </div>
            <div>
              <Label className="text-xs">Overlay darkness · {Math.round(form.login_overlay_opacity * 100)}%</Label>
              <input
                type="range" min="0" max="0.85" step="0.05"
                value={form.login_overlay_opacity}
                onChange={(e) => setForm({ ...form, login_overlay_opacity: Number(e.target.value) })}
                className="w-full accent-cyan-600 mt-1"
                data-testid="gradient-overlay"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">Higher = more dramatic, better text contrast.</p>
            </div>
          </div>
        </div>

        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-login">Save Login Page</Button>
      </Card>

      {/* ========== ATTENDANCE PROVIDERS (Facial / Biometric / Geo-fence) ========== */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm space-y-4 bg-white" data-testid="attendance-providers">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Attendance Providers</div>
            <p className="text-xs text-slate-500 mt-1">Geo-fenced selfie attendance, biometric devices and remote policy. Backend integration coming next sprint.</p>
          </div>
          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Basic Version</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { k: "selfie",    title: "Selfie + Geo-fence",   sub: "Browser camera + GPS",      status: "ready",   chip: "Built-in" },
            { k: "biometric", title: "Biometric (BioMetrix)",sub: "RFID / Fingerprint device", status: "soon",    chip: "Backend Pending" },
            { k: "facial",    title: "Facial Recognition",   sub: "AWS Rekognition / on-device", status: "soon", chip: "Backend Pending" },
            { k: "rfid",      title: "RFID / NFC",           sub: "Mifare cards · access controllers", status: "soon", chip: "Backend Pending" },
            { k: "wifi",      title: "Office Wi-Fi check",   sub: "SSID match for in-office",   status: "ready",   chip: "Built-in" },
            { k: "manual",    title: "Manual / Remote",      sub: "Punch from anywhere",        status: "ready",   chip: "Built-in" },
          ].map((p) => (
            <div key={p.k} className="p-4 rounded-xl border border-slate-200 bg-white" data-testid={`prov-${p.k}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold font-[Outfit] text-slate-900 text-sm">{p.title}</div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${p.status === "ready" ? "text-emerald-700 bg-emerald-50" : "text-slate-500 bg-slate-100"}`}>{p.chip}</span>
              </div>
              <div className="text-[12px] text-slate-600">{p.sub}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Geo-fence radius (m)</Label>
            <Input type="number" value={form.geo_radius_m || 200} onChange={(e) => setForm({ ...form, geo_radius_m: Number(e.target.value) })} className="mt-1" data-testid="geo-radius" />
          </div>
          <div>
            <Label>Selfie required</Label>
            <select className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" value={form.selfie_required ? "yes" : "no"} onChange={(e) => setForm({ ...form, selfie_required: e.target.value === "yes" })} data-testid="selfie-req">
              <option value="yes">Required</option>
              <option value="no">Optional</option>
            </select>
          </div>
          <div>
            <Label>Device verification</Label>
            <select className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white" value={form.device_verification || "trusted"} onChange={(e) => setForm({ ...form, device_verification: e.target.value })} data-testid="device-verify">
              <option value="trusted">Trusted device only</option>
              <option value="any">Any device</option>
              <option value="otp">OTP each session</option>
            </select>
          </div>
        </div>
        <Button onClick={save} className="qh-grad-bg text-white font-semibold" data-testid="save-providers">Save Providers</Button>
      </Card>

      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm bg-white">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">Your Account</div>
        <div className="space-y-2 text-sm">
          <div><span className="text-slate-500">Email:</span> <span className="font-medium text-slate-900">{user?.email}</span></div>
          <div><span className="text-slate-500">Role:</span> <span className="font-medium text-slate-900 capitalize">{user?.role?.replace("_"," ")}</span></div>
          <div><span className="text-slate-500">Employee ID:</span> <span className="font-medium text-slate-900">{user?.employee_id}</span></div>
        </div>
      </Card>

      {/* Logo Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg" data-testid="logo-preview-dialog">
          <DialogHeader><DialogTitle>Logo preview</DialogTitle></DialogHeader>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 flex items-center justify-center">
            {form.logo_url ? <img src={form.logo_url} alt="Logo preview" className="max-w-full max-h-72 object-contain" /> : <span className="text-slate-400 text-sm">No logo uploaded</span>}
          </div>
          <p className="text-[11px] text-slate-500 text-center">This is how your logo will appear across the app sidebar, login page, and notifications.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ColorPick({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 rounded border border-slate-200 bg-white cursor-pointer"
          data-testid={`${testId}-swatch`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 font-mono text-xs"
          data-testid={`${testId}-hex`}
        />
      </div>
    </div>
  );
}
